import classNames from 'classnames';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WebFont from 'webfontloader';

import { lastMagazineIsVideo, lastPageBlockColor } from '../../lib/utils';

import { useLayout, useSite } from '../../contexts';

import type { Magazine, Page } from '../../types/entities';
import DistrictIcon from '../icons/District';
import MapIcon from '../icons/Map';
import Cookies from '../partials/Cookies';
import Footer from '../partials/Footer';
import Header from '../partials/Header';
import LoadingBar from '../partials/LoadingBar';
import Popup from '../partials/Popup';

import styles from '../../../styles/layouts/main.module.css';

interface MainLayoutProps {
    children: React.ReactNode;
    page?: Page | null;
}

interface Fonts {
    custom?: {
        families: string[];
    };
    google?: {
        [key: string]: string[];
    };
}

const DEFAULT_FONTS: Fonts = {
    custom: {
        families: ['TTCommons-Regular', 'TTCommons-DemiBold', 'TTCommons-Bold', 'HeadingPro-Heavy'],
    },
};

const isFooterSplashWhite = (page: Page | null, magazines: Magazine[]): boolean => {
    if (!page) return false;
    const { handle = null, type = null, blocks = null } = page || {};

    return (
        type === 'magazine' ||
        type === 'magazine_photo' ||
        type === 'magazine_podcast' ||
        (type === 'magazine_video' &&
            blocks !== null &&
            blocks !== undefined &&
            blocks.length > 0) ||
        lastPageBlockColor(page) === 'white' ||
        (handle === 'magazine' && !lastMagazineIsVideo(magazines || null))
    );
};

const isFooterSplashGrey = (page: Page | null): boolean => {
    if (!page) return false;
    const { handle = null, type = null } = page || {};
    if (type === 'district') return false;

    return (
        handle === 'districts' ||
        handle === 'directory' ||
        type === 'list_contacts' ||
        type === 'list_districts' ||
        lastPageBlockColor(page) === 'grey'
    );
};

const hasMapBackground = (page: Page | null): boolean => {
    if (!page) return false;
    const handles = ['contact', 'partners', 'events'];
    return page.handle !== undefined && handles.indexOf(page.handle) !== -1;
};

const hasDistrictBackground = (page: Page | null): boolean => {
    if (!page) return false;
    return page.type === 'district';
};

function MainLayout({ page = null, children }: MainLayoutProps) {
    const {
        fontsLoaded,
        setFontsLoaded,
        setSize,
        // setScroll,
        menuOpened,
        popupClosed,
        setPopupClosed,
        footerIsPurple,
    } = useLayout();

    const {
        statusCode = null,
        menus = null,
        popup = null,
        showCookieConsent = null,
        setShowCookieConsent = null,
    } = useSite();

    const {
        headerTopMenu = null,
        socialMenu = null,
        headerMainMenu = null,
        footerMenu = null,
    } = menus || {};

    const magazineItems = []; //

    const [footerSplashIsGrey, setFooterSplashIsGrey] = useState(false);
    const [hasMap, setHasMap] = useState(false);
    const [hasDistrict, setHasDistrict] = useState(false);
    const [footerIsWhite, setFooterIsWhite] = useState(false);

    // Update size
    const updateSize = useCallback(() => {
        setSize({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    }, [setSize]);

    // Update scroll
    // const updateScroll = useCallback(() => {
    //     const { x, y } = getScroll();
    //     setScroll({ x, y });
    // }, [setScroll]);

    // Throttled versions
    const updateSizeThrottled = useMemo(
        () =>
            throttle(updateSize, 1000 / 10, {
                leading: true,
                trailing: true,
            }),
        [updateSize],
    );

    // const updateScrollThrottled = useMemo(
    //     () =>
    //         throttle(updateScroll, 1000 / 20, {
    //             leading: true,
    //             trailing: true,
    //         }),
    //     [updateScroll],
    // );

    // Font loading
    useEffect(() => {
        const fonts = DEFAULT_FONTS;
        const fontsTimeout = 2000;

        WebFont.load({
            ...fonts,
            timeout: fontsTimeout,
            active: () => setFontsLoaded(true),
            inactive: () => setFontsLoaded(true),
        });
    }, [setFontsLoaded]);

    // Window resize listener
    useEffect(() => {
        const onResize = () => updateSizeThrottled();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            updateSizeThrottled.cancel();
        };
    }, [updateSizeThrottled]);

    // Update page-dependent state
    useEffect(() => {
        if (page !== null) {
            setFooterSplashIsGrey(isFooterSplashGrey(page));
            setFooterIsWhite(isFooterSplashWhite(page, magazineItems));
            setHasMap(hasMapBackground(page));
            setHasDistrict(hasDistrictBackground(page));
        }
    }, [page, magazineItems, setFooterSplashIsGrey, setFooterIsWhite, setHasMap, setHasDistrict]);

    // Menu items with current flag
    const topMenuItems = useMemo(
        () =>
            (headerTopMenu || [])
                .filter((it) => it !== null)
                .map((it) => ({
                    ...it,
                    current: page !== null && page?.url === it?.url,
                })),
        [headerTopMenu, page],
    );

    const mainMenuItems = useMemo(
        () =>
            (headerMainMenu || [])
                .filter((it) => it !== null)
                .map((it) => ({
                    ...it,
                    current: page !== null && page?.url === it?.url,
                })),
        [headerMainMenu, page],
    );

    // Computed values
    const footerSplashIsWhite = footerIsWhite && !footerIsPurple;
    const ready = fontsLoaded;
    const showPopup = popup !== null && page !== null && page?.type === 'list_events';
    const isNewsletter = page !== null && page.type === 'newsletter';

    const style = {
        opacity: ready ? 1 : 0,
    };

    const onPopupClickClose = useCallback(() => {
        setPopupClosed(true);
    }, [setPopupClosed]);

    const onCookiesClose = useCallback(() => {
        setShowCookieConsent(false);
    }, [setShowCookieConsent]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hasPopup]: showPopup && !popupClosed,
                },
            ])}
            style={style}
        >
            <a href="#main-content" className="sr-only">
                Aller au contenu principal
            </a>
            <div className={styles.inner}>
                <div className={styles.loading}>
                    <LoadingBar
                        loading={page === null && statusCode === null}
                        loaded={page !== null || statusCode !== null}
                    />
                </div>
                <header className={styles.header}>
                    <Header
                        homeLink="/fr"
                        topMenuItems={topMenuItems}
                        socialMenuItems={socialMenu}
                        mainMenuItems={mainMenuItems}
                        menuOpened={menuOpened}
                    />
                </header>
                <main
                    id="main-content"
                    className={classNames([
                        styles.content,
                        { [styles.isNewsletter]: isNewsletter },
                    ])}
                >
                    {ready ? children : null}
                </main>
                <footer className={styles.footer}>
                    <Footer
                        menuColumns={footerMenu}
                        splashIsGrey={footerSplashIsGrey}
                        splashIsWhite={footerSplashIsWhite}
                        newsletterIsLarge={isNewsletter}
                    />
                </footer>
            </div>
            <div className={styles.background}>
                <div className={styles.inner}>
                    <div
                        className={classNames([
                            styles.map,
                            {
                                [styles.visible]: hasMap,
                            },
                        ])}
                    >
                        <MapIcon className={styles.icon} />
                    </div>
                    <div
                        className={classNames([
                            styles.district,
                            {
                                [styles.visible]: hasDistrict,
                            },
                        ])}
                    >
                        <DistrictIcon
                            name={
                                hasDistrict && page !== null && page.type === 'district'
                                    ? page.handle
                                    : null
                            }
                            className={styles.icon}
                        />
                    </div>
                </div>
            </div>
            {showPopup ? (
                <div
                    className={classNames([
                        styles.popupContainer,
                        {
                            [styles.closed]: popupClosed,
                        },
                    ])}
                >
                    <div className={styles.popup}>
                        <Popup {...popup} page={page} onClickClose={onPopupClickClose} />
                    </div>
                </div>
            ) : null}
            {showCookieConsent ? (
                <div className={classNames([styles.cookiesContainer])}>
                    <div className={styles.cookies}>
                        <Cookies onClose={onCookiesClose} />
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default MainLayout;
